import React, { useEffect, useState } from "react";
import { apiV2Service } from "../../services/apiV2Service";
import { useHistory, Link, useLocation } from "react-router-dom";
import queryString from "query-string";
import { showApiMessage } from "../../utils/messageHandler";
import logger from "../../utils/logger";
import { useAnalytics } from "../../components/Analytics";
import { AnalyticsEvents, AnalyticsProps } from "../../utils/analytics";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "../../shadcn/ui/card";
import { AlertCircle, KeyRound } from "lucide-react";

import { getWebOriginForFlavor } from "../../utils/deepLink";

const ResetPassword = () => {
  const [user, setUser] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { trackEvent } = useAnalytics();

  // Parse query params for password expired flow
  const search = queryString.parse(location.search);
  const reason = search.reason;
  const emailFromQuery = search.email;
  const isPasswordExpired = reason === "password_expired";

  useEffect(() => {
    // Pre-fill email from query param if available
    if (emailFromQuery) {
      setUser(emailFromQuery);
    }
  }, [emailFromQuery]);

  useEffect(() => {
    // Track page view
    trackEvent(AnalyticsEvents.PASSWORD_RESET_REQUESTED, {
      [AnalyticsProps.PAGE_NAME]: isPasswordExpired ? "password_expired_page" : "reset_password_page",
      page_load: true,
    });
  }, [trackEvent, isPasswordExpired]);

  const resetButton = async () => {
    if (!user) return;

    setIsLoading(true);

    // Track reset password request
    trackEvent(AnalyticsEvents.PASSWORD_RESET_REQUESTED, {
      [AnalyticsProps.EMAIL]: user,
      action: "button_clicked",
    });

    try {
      // Construct confirmation URL pointing to root with action param (required for mobile deep links)
      // Using flavor-aware origin ensures consistency though normally results in standard domain on web
      const confirmationUrl = `${getWebOriginForFlavor()}/?action=reset_password`;

      const response = await apiV2Service.forgotPassword({
        username: user,
        confirmationUrl: confirmationUrl,
      });
      logger.log(response);
      showApiMessage(response, "reset password");

      // Track successful password reset email
      trackEvent(AnalyticsEvents.PASSWORD_RESET_EMAIL_SENT, {
        [AnalyticsProps.EMAIL]: user,
        [AnalyticsProps.IS_SUCCESS]: true,
      });

      history.push("/gotomail");
    } catch (err) {
      logger.log(err.response?.data);
      showApiMessage(err, "reset password");

      // Track failed password reset
      trackEvent(AnalyticsEvents.PASSWORD_RESET_FAILED, {
        [AnalyticsProps.EMAIL]: user,
        [AnalyticsProps.ERROR_REASON]: err.response?.data?.message || "An error occurred",
        [AnalyticsProps.ERROR_MESSAGE]: JSON.stringify(err.response?.data || {}),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            {isPasswordExpired ? (
              <>
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                  <KeyRound className="h-6 w-6 text-amber-600" />
                </div>
                <CardTitle className="text-xl md:text-2xl">Password Change Required</CardTitle>
                <CardDescription>
                  Your password has expired and needs to be changed for security reasons. Enter your username to receive
                  a reset link.
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-xl md:text-2xl">Reset your password</CardTitle>
                <CardDescription>Enter your username and we'll send you a link to reset your password</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {isPasswordExpired && (
              <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  For your security, passwords must be updated periodically. Please reset your password to continue
                  accessing your account.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={user}
                onChange={e => setUser(e.target.value)}
                onKeyDown={e => e.key === "Enter" && resetButton()}
              />
            </div>
            <Button type="button" onClick={resetButton} disabled={!user || isLoading} className="w-full">
              {isLoading ? "Sending..." : "Send Reset Link"}
            </Button>
          </CardContent>
          <CardFooter className="justify-center">
            <Link to="/signin" className="text-sm text-primary hover:underline">
              Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
